import React from 'react'
import styled, { spin } from '../../constants/theme'
import { propsUpdated, stateUpdated } from '../../helpers/shouldUpdate'

const sharedDefaultStyles: any = {
  '&.asOutline': {
    background: 'transparent',
  },
  '&.big': {
    borderRadius: 25,
    height: 50,
    lineHeight: 3.3,
    padding: '0 30px',
  },
  '&.saving': {
    '&.big': {
      '&:after': {
        borderWidth: 4,
        height: 40,
        left: 3,
        top: 3,
        width: 40,
      },
      padding: 0,
      width: 50,
    },
    '&.small': {
      '&:after': {
        borderWidth: 2,
        height: 16,
        width: 16,
      },
      padding: 0,
      width: 24,
    },
    '&:after': {
      animation: `${spin} 0.666s infinite linear`,
      borderRadius: '50%',
      borderRight: '3px solid transparent',
      borderTop: '3px solid transparent',
      height: 24,
      left: 2,
      opacity: 1,
      position: 'absolute',
      top: 2,
      width: 24,
    },
    padding: 0,
    position: 'relative',
    width: 32,
  },
  '&.small': {
    borderRadius: 12,
    height: 24,
    lineHeight: 1.4,
    padding: '0 15px',
  },
  '&:after': {
    content: '""',
    opacity: 0,
  },
  borderRadius: 16,
  borderStyle: 'solid',
  borderWidth: 2,
  display: 'inline-block',
  fontSize: 14,
  fontWeight: 900,
  height: 32,
  lineHeight: 2,
  padding: '0 20px',
  textAlign: 'center',
  textTransform: 'uppercase',
  transitionProperty: 'background-color, color, min-width, opacity, width',
  whiteSpace: 'nowrap',
}

const sharedThemedStyles = ({ theme }): any => ({
  '&.asGreen': {
    '&.asOutline': {
      background: 'transparent',
      color: theme.success,
    },
    background: theme.success,
    borderColor: theme.success,
  },
  '&.asGreen:focus, &.asGreen:hover, &.asGreen.asOutline:focus, &.asGreen.asOutline:hover': {
    background: theme.successDark,
    color: theme.textLight,
  },
  '&.asGrey:focus, &.asGrey:hover, &.asGrey.asOutline:focus, &.asGrey.asOutline:hover': {
    background: theme.text,
    color: theme.textLight,
  },
  '&.asOutline': {
    color: theme.primary,
  },
  '&.asRed': {
    '&.asOutline': {
      background: 'transparent',
      color: theme.error,
    },
    background: theme.error,
    borderColor: theme.error,
  },
  '&.asWhite': {
    '&.asOutline': {
      background: 'transparent',
      color: theme.textLight,
      borderColor: theme.textLight,
    },
    background: theme.textLight,
    color: theme.text,
    borderColor: theme.text,
  },
  '&.asRed:focus, &.asRed:hover, &.asRed.asOutline:focus, &.asRed.asOutline:hover': {
    background: theme.errorDark,
    color: theme.textLight,
  },
  '&.asWhite:focus, &.asWhite:hover': {
    background: theme.greyMediumLight,
    color: theme.textLight,
  },
  '&.asWhite.asOutline:focus, &.asWhite.asOutline:hover': {
    background: theme.greyLight,
    color: theme.text,
  },
  '&.errored, &.errored:hover, &.errored[disabled], &.errored[disabled]:hover': {
    '&.asOutline': {
      color: theme.error,
    },
    background: theme.error,
    borderColor: theme.error,
  },
  '&.saved, &.saved:hover, &.saved[disabled], &.saved[disabled]:hover': {
    '&.asOutline': {
      color: theme.success,
    },
    background: theme.success,
    borderColor: theme.success,
  },
  '&.saving': {
    '&.asGreen.asOutline:after': {
      borderBottomColor: theme.success,
      borderLeftColor: theme.success,
    },
    '&.asGrey.asOutline:after': {
      borderBottomColor: theme.text,
      borderLeftColor: theme.text,
    },
    '&.asOutline:after': {
      borderBottomColor: theme.primary,
      borderLeftColor: theme.primary,
    },
    '&.asRed.asOutline:after': {
      borderBottomColor: theme.error,
      borderLeftColor: theme.error,
    },
    '&.asWhite.asOutline:after': {
      borderBottomColor: theme.textLight,
      borderLeftColor: theme.textLight,
    },
    '&:after': {
      borderBottom: `3px solid ${theme.textLight}`,
      borderLeft: `3px solid ${theme.textLight}`,
    },
    '&[disabled], &[disabled]:hover': {
      '&.asGreen': {
        '&.asOutline': {
          background: 'transparent',
          color: theme.success,
        },
        background: theme.success,
        borderColor: theme.success,
      },
      '&.asGrey': {
        '&.asOutline': {
          background: 'transparent',
          color: theme.text,
        },
        background: theme.grey,
        borderColor: theme.grey,
      },
      '&.asOutline': {
        background: 'transparent',
        color: theme.primary,
      },
      '&.asRed': {
        '&.asOutline': {
          background: 'transparent',
          color: theme.error,
        },
        background: theme.error,
        borderColor: theme.error,
      },
      '&.asWhite': {
        '&.asOutline': {
          background: 'transparent',
          color: theme.textLight,
        },
        background: theme.grey,
        borderColor: theme.grey,
      },
      background: theme.primary,
      borderColor: theme.primary,
    },
  },
  '&:after': {
    transition: `opacity ${theme.speed} ${theme.ease} ${theme.speed.replace(
      's',
      '',
    ) / 2}s`,
  },
  '&:focus, &:hover': {
    '&:asOutline': {
      background: 'transparent',
      color: theme.primary,
    },
    background: theme.primaryDark,
    color: theme.textLight,
  },
  '&[disabled], &[disabled]:hover, &.asGrey': {
    '&.asOutline': {
      background: 'transparent',
      color: theme.text,
    },
    background: theme.grey,
    borderColor: theme.grey,
  },
  background: theme.primary,
  borderColor: theme.primary,
  color: theme.textLight,
  transitionDuration: theme.speed,
  transitionTimingFunction: theme.ease,
})

interface BtnProps {
  title?: string
}

const Button = styled.button<BtnProps>(sharedDefaultStyles, sharedThemedStyles)
const Link = styled.a<BtnProps>(sharedDefaultStyles, sharedThemedStyles)

interface Props {
  className?: string
  disabled?: boolean
  href?: string
  id?: string
  isErrored?: boolean
  isLink?: boolean
  isSaving?: boolean
  onClick?: (e: any) => any
  rel?: string
  target?: string
  title?: string
  type?: 'reset' | 'submit' | 'button' | undefined
}

enum ButtonState {
  ERRORED = 'errored',
  NORMAL = 'normal',
  SAVED = 'saved',
  SAVING = 'saving',
}

interface State {
  state: ButtonState
}

export const SAVED_TIMEOUT = 3000

class PillButton extends React.Component<Props, State> {
  timeout: any

  constructor(props) {
    super(props)
    this.state = { state: ButtonState.NORMAL }
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.isSaving && this.props.isSaving) {
      this.setState({ state: ButtonState.SAVING })
    } else if (prevProps.isSaving && !this.props.isSaving) {
      this.setState(
        {
          state: this.props.isErrored ? ButtonState.ERRORED : ButtonState.SAVED,
        },
        () => {
          this.timeout = setTimeout(() => {
            this.setState({ state: ButtonState.NORMAL })
          }, SAVED_TIMEOUT)
        },
      )
    }
  }

  shouldComponentUpdate(nextProps: Props, nextState: State) {
    return (
      propsUpdated(
        [
          'children',
          'className',
          'disabled',
          'isErrored',
          'isLink',
          'isSaving',
          'onClick',
        ],
        this.props,
        nextProps,
      ) || stateUpdated(['state'], this.state, nextState)
    )
  }

  componentWillUnmount() {
    clearTimeout(this.timeout)
  }

  getTitle = () => {
    switch (this.state.state) {
      case ButtonState.ERRORED:
        return 'Error'
      case ButtonState.SAVING:
        return null
      case ButtonState.SAVED:
        return 'Updated'
      default:
        return this.props.children
    }
  }

  render() {
    const {
      className,
      disabled,
      href,
      id,
      isLink,
      onClick,
      rel,
      target,
      title,
      type,
    } = this.props
    const { state } = this.state
    const buttonText: any = this.getTitle()
    const sharedProps = {
      className: `Pill ${className ? className : ''} ${state}`,
      disabled: disabled || state !== ButtonState.NORMAL,
      id,
      onClick,
      title: title || buttonText,
    }
    return isLink ? (
      <Link
        aria-label={buttonText}
        aria-live="polite"
        href={href}
        id={id}
        rel={rel}
        role="button"
        target={target}
        {...sharedProps}
      >
        {buttonText}
      </Link>
    ) : (
      <Button aria-live="polite" type={type ? type : 'button'} {...sharedProps}>
        {buttonText}
      </Button>
    )
  }
}

export default PillButton
